import React, { useState, useEffect, useMemo, useCallback, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getPersonaChipClass } from '../../../../utils/styles.js';
import { useAuth } from '../../../hooks/useAuth';

// NUESTROS COMPONENTES DE ARQUITECTURA MODULAR COMUNES
import SortableHeader from '../common/SortableHeader';
import PaginatorFooter from '../common/PaginatorFooter';
import ColumnSelector from '../common/ColumnSelector';
import TableContainer from '../common/TableContainer';
import Icons from '../common/Icons';

// MODALES EXISTENTES EN EL PROYECTO
import ModalEmision from '../../../ui/ModalEmision.jsx'; 
import ModalEdicionContrato from '../../../ui/ModalEdicionContrato.jsx'; 
import ModalDetallePoliza from '../../../ui/ModalDetallePoliza.jsx';

// FILTROS Y CONTROLADOR
import FiltrosDinamicos from '../../../ui/FiltrosDinamicos.jsx';
import { usePendientesTable } from './usePendientesTable';

// --- FILA MEMOIZADA ---
const TableRow = memo(({ item, isLocked, isLockedByMe, isSelected, columnVisibility, onSelect, onAction, onUnlock }) => {
    const estado = item.estadoEmision || item.estadoPoliza;

    return (
        <tr 
            onClick={() => !isLocked && onSelect(item.id)} 
            className={`group cursor-pointer transition-all duration-75 border-b border-slate-100 
                ${isLocked ? 'opacity-40 select-none bg-slate-50' : ''}
                ${isSelected ? 'bg-blue-100/60' : 'hover:bg-slate-50'}`}
        >
            {columnVisibility.fechaCarga && (
                <td className="p-2.5 px-3 border-b border-r border-slate-100 text-center text-xs font-bold text-slate-500 tabular-nums">
                    {item.fechaCarga?.split('-').reverse().join('/')}
                </td>
            )}
            
            {columnVisibility.displayNombre && (
                <td className="p-2.5 px-4 text-sm text-slate-900 font-bold border-b border-r border-slate-100 truncate max-w-[280px]" title={item.displayNombre}>
                    {item.displayNombre}
                </td>
            )}
            
            {columnVisibility.displayDoc && (
                <td className="p-2.5 px-3 text-sm text-slate-600 font-mono text-center border-b border-r border-slate-100">{item.displayDoc}</td>
            )}
            
            {columnVisibility.motivo && (
                <td className="p-2.5 px-3 border-b border-r border-slate-100 text-center text-[11px] font-bold uppercase text-slate-600">{item.motivo || item.tipoOperacion || '-'}</td>
            )}
            
            {columnVisibility.estadoEmision && (
                <td className="p-2.5 px-3 border-b border-r border-slate-100 text-center">
                    <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase border ${
                        estado === 'PENDIENTE DE EMISIÓN' ? 'bg-orange-50 text-orange-700 border-orange-200' :
                        estado === 'PENDIENTE DE INSPECCIÓN' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                        'bg-indigo-50 text-indigo-700 border-indigo-200'
                    }`}>
                        {estado}
                    </span>
                </td>
            )}
            
            {columnVisibility.nPoliza && (
                <td className="p-2.5 px-3 text-sm text-slate-900 font-bold text-center border-b border-r border-slate-100 tabular-nums">{item.nPoliza || item.nContrato || '-'}</td>
            )}
            
            {columnVisibility.compania && (
                <td className="p-2.5 px-3 border-b border-r border-slate-100 text-center">
                    <div className="flex items-center justify-center">
                        <div className="flex items-center gap-2 px-3 py-1 bg-blue-50 border border-blue-100 rounded-lg w-fit group-hover:bg-white transition-colors">
                            <div className="text-blue-400"><Icons.Company /></div>
                            <span className="text-[11px] text-blue-800 font-bold uppercase truncate max-w-[160px]">{item.compania}</span>
                        </div>
                    </div>
                </td>
            )}
            
            {columnVisibility.riesgo && (
                <td className="p-2.5 px-4 text-sm text-slate-700 border-b border-r border-slate-100">
                    <div className="flex flex-col text-left">
                        <span className="font-bold text-slate-800 tracking-tight">{item.riesgo || 'ART'}</span>
                        <span className="text-[11px] text-slate-500 font-medium uppercase truncate mt-0.5">{item.subriesgo || 'General'}</span>
                    </div>
                </td>
            )}
            
            {columnVisibility.suscriptor && (
                <td className="p-2.5 px-3 text-sm text-slate-900 font-medium text-left border-b border-r border-slate-100 tabular-nums">{item.suscriptor || '-'}</td>
            )}
            
            {columnVisibility.canal && (
                <td className="p-2.5 px-4 text-sm text-slate-700 border-b border-r border-slate-100">
                    <div className="flex flex-col text-left">
                        <span className="font-bold text-slate-800 tracking-tight">{item.canal || '-'}</span>
                        <span className="text-[11px] text-slate-500 font-medium uppercase truncate mt-0.5">{item.canalNombreUnified || '-'}</span>
                    </div>
                </td>
            )}

            {/* Acciones */}
            <td className="p-2.5 px-4 text-right sticky right-0 transition-colors z-20 border-b border-l border-slate-100 shadow-[-10px_0_15px_-5px_rgba(0,0,0,0.03)] bg-white group-hover:bg-slate-50">
                <div className="flex justify-end items-center gap-2">
                    {isLocked ? (
                        <div className="flex items-center gap-2 text-amber-600 bg-amber-50 px-3 py-1.5 rounded-lg border border-amber-100 animate-pulse shadow-sm">
                            <Icons.Lock /> <span className="text-[9px] font-black uppercase tracking-tighter">{item.bloqueo?.userName || 'Ocupado'}</span>
                        </div>
                    ) : (
                        <>
                            <button onClick={(e) => { e.stopPropagation(); onAction(item, 'view'); }} className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all active:scale-90" title="Ver Detalle"><Icons.User /></button>
                            <button onClick={(e) => { e.stopPropagation(); onAction(item, 'edit'); }} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all active:scale-90" title="Editar Pendiente"><Icons.Edit /></button>
                            {isLockedByMe && (
                                <button onClick={(e) => { e.stopPropagation(); onUnlock(item.path); }} className="p-1.5 text-red-300 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all" title="Liberar Caso">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path></svg>
                                </button>
                            )}
                        </>
                    )}
                </div>
            </td>
        </tr>
    );
});
TableRow.displayName = 'TableRow';

// --- ORQUESTADOR VISUAL MÁSTER ---
const TableroPendientes = () => {
    const navigate = useNavigate();
    const { user } = useAuth();

    // Consumimos el cerebro de pendientes
    const {
        loading,
        clientsCache,
        activeItem,
        showEditPoliza,
        showEditArt,
        showDetail,
        tableState,
        itemsTable,
        activeItemPathRef,
        setShowEditPoliza,
        setShowEditArt,
        setShowDetail,
        setActiveItem,
        handleAction,
        unlockPoliza
    } = usePendientesTable();

    // Desestructuramos el estado de tabla genérico
    const {
        sortConfig,
        columnVisibility,
        currentPage,
        setCurrentPage,
        selectedRowId,
        setSelectedRowId,
        filterValues,
        filteredData,
        paginatedData,
        totalPages,
        handleSort,
        toggleColumn,
        handleFilterChange
    } = tableState;

    const columnLabels = {
        fechaCarga: "Carga", displayNombre: "Cliente", displayDoc: "DNI/CUIT",
        motivo: "Operación", estadoEmision: "Estado", nPoliza: "N° Póliza/Cont",
        compania: "Compañía", riesgo: "Riesgo/Sub", suscriptor: "Suscriptor", canal: "Canal"
    };

    // Liberación por descarga involuntaria
    useEffect(() => {
        const handleUnload = () => { if (activeItemPathRef.current) unlockPoliza(activeItemPathRef.current); };
        window.addEventListener('beforeunload', handleUnload);
        return () => window.removeEventListener('beforeunload', handleUnload);
    }, [unlockPoliza, activeItemPathRef]);

    // Configuración de Filtros Dinámicos
    const filterConfig = useMemo(() => {
        const getUnique = (list, key) => [...new Set(list.map(p => p[key]))].filter(Boolean).sort().map(v => ({label: v, value: v}));
        
        const selRiesgo = filterValues.riesgo || [];
        const dataForSubriesgo = selRiesgo.length > 0 ? itemsTable.filter(p => selRiesgo.includes(p.riesgo)) : itemsTable;
        const subriesgoOptions = getUnique(dataForSubriesgo, 'subriesgo');

        const selCanal = filterValues.canal || [];
        const dataForCanalNombre = selCanal.length > 0 ? itemsTable.filter(d => selCanal.includes(d.canal)) : itemsTable;
        const canalNombreOptions = getUnique(dataForCanalNombre, 'canalNombreUnified');

        const selCanalNombre = filterValues.canalNombre || [];
        const dataForSubcanal = selCanalNombre.length > 0 ? dataForCanalNombre.filter(d => selCanalNombre.includes(d.canalNombreUnified)) : dataForCanalNombre;
        const subcanalOptions = getUnique(dataForSubcanal, 'subcanalNombre');

        return [
            { name: 'search', label: 'Buscador', type: 'text', placeholder: 'Póliza, Cliente o DNI...', className: 'w-72' },
            { name: 'riesgo', label: 'Riesgo', type: 'select', isMulti: true, options: getUnique(itemsTable, 'riesgo') },
            { name: 'subriesgo', label: 'Subriesgo', type: 'select', isMulti: true, options: subriesgoOptions },
            { name: 'estado', label: 'Estado Póliza', type: 'select', isMulti: true, options: [...new Set(itemsTable.map(d => d.estadoEmision || d.estadoPoliza))].sort().map(v => ({label: v, value: v})) },
            { name: 'compania', label: 'Compañía', type: 'select', isMulti: true, options: getUnique(itemsTable, 'compania') },
            { name: 'canal', label: 'Canal', type: 'select', isMulti: true, options: getUnique(itemsTable, 'canal') },
            { name: 'canalNombre', label: 'Nombre Canal', type: 'select', isMulti: true, options: canalNombreOptions },
            { name: 'subcanalNombre', label: 'Subcanal / Asociado', type: 'select', isMulti: true, options: subcanalOptions },
            { name: 'suscriptor', label: 'Suscriptor', type: 'select', isMulti: true, options: getUnique(itemsTable, 'suscriptor') },
            { name: 'fecha', label: 'Fecha Carga', type: 'date-range' },
        ];
    }, [itemsTable, filterValues]);

    if (loading && itemsTable.length === 0) return (
        <div className="flex h-screen flex-col items-center justify-center bg-white space-y-4">
            <div className="w-12 h-12 border-4 border-indigo-900/20 border-t-indigo-900 rounded-full animate-spin"></div>
            <p className="text-[10px] font-black uppercase tracking-widest text-indigo-900">Sincronizando pendientes...</p>
        </div>
    );

    return (
        <div className="flex h-screen w-full flex-col overflow-hidden bg-white font-['Inter'] antialiased text-left text-slate-900">
            <div className="h-1 w-full bg-[#01032d] shrink-0"></div>
            
            {/* CABECERA UNIFICADA */}
            <header className="flex h-20 shrink-0 items-center justify-between border-b border-slate-200 px-8 bg-white z-[110]">
                <div className="flex items-center gap-6">
                    <button onClick={() => navigate('/cpanel')} className="p-2 text-slate-400 hover:text-slate-800 hover:bg-slate-50 rounded-lg transition-all border border-transparent hover:border-slate-200 shadow-sm"><Icons.ArrowLeft /></button>
                    <h2 className="text-xl font-bold text-slate-800 uppercase tracking-tight">Tablero Pendientes</h2>
                </div>
                <div className="flex items-center gap-4">
                    <ColumnSelector 
                        columnVisibility={columnVisibility} 
                        columnLabels={columnLabels} 
                        toggleColumn={toggleColumn} 
                    />
                    <div className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-xl p-1 pr-4 shadow-sm">
                        <div className="bg-slate-900 text-white px-4 py-1.5 rounded-lg text-lg font-black tabular-nums">{filteredData.length}</div>
                        <span className="text-[10px] font-bold uppercase text-slate-500">Casos</span>
                    </div>
                </div>
            </header>

            {/* FILTROS DINÁMICOS CON KEY ESTABLE */}
            <FiltrosDinamicos 
                key="pendientes-table-filters"
                config={filterConfig} 
                onFilterChange={handleFilterChange} 
                defaultValues={filterValues} 
            />

            {/* TABLA PRINCIPAL RESPONSIVE */}
            <main className="flex-grow overflow-hidden flex flex-col bg-white">
                <TableContainer minWidth="2200px">
                    <thead>
                        <tr className="bg-[#01032d] text-white">
                            <SortableHeader label="Carga" sortKey="fechaCarga" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-28 text-center" />
                            <SortableHeader label="Cliente" sortKey="displayNombre" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-[320px] text-left" />
                            <SortableHeader label="DNI / CUIT" sortKey="displayDoc" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-32 text-center" />
                            <SortableHeader label="Operación" sortKey="motivo" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-36 text-center" />
                            <SortableHeader label="Estado" sortKey="estadoEmision" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-44 text-center" />
                            <SortableHeader label="N° Póliza/Cont" sortKey="nPoliza" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-40 text-center" />
                            <SortableHeader label="Compañía" sortKey="compania" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-48 text-center" />
                            <SortableHeader label="Riesgo / Sub" sortKey="riesgo" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-[240px] text-left" />
                            <SortableHeader label="Suscriptor" sortKey="suscriptor" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-40 text-left" />
                            <SortableHeader label="Canal / Nombre" sortKey="canal" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-48 text-left" />
                            <th className="w-24 p-4 px-4 text-center sticky right-0 top-0 z-[65] bg-slate-900 border-b border-slate-700 uppercase tracking-wider text-[10px] font-semibold text-white shadow-[-2px_0_5px_rgba(0,0,0,0.1)]">Acciones</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {paginatedData.map(item => {
                            const isLocked = tableState.checkIsLocked(item, user?.uid);
                            const isLockedByMe = item.bloqueo && item.bloqueo.userId === user?.uid;

                            return (
                                <TableRow 
                                    key={item.id} 
                                    item={item} 
                                    isSelected={selectedRowId === item.id} 
                                    isLocked={isLocked}
                                    isLockedByMe={isLockedByMe}
                                    columnVisibility={columnVisibility}
                                    onSelect={setSelectedRowId} 
                                    onAction={handleAction} 
                                    onUnlock={unlockPoliza}
                                />
                            );
                        })}
                    </tbody>
                </TableContainer>

                {/* PIE DE PAGINACIÓN UNIVERSAL */}
                <PaginatorFooter 
                    currentPage={currentPage}
                    totalPages={totalPages}
                    currentLength={paginatedData.length}
                    totalLength={filteredData.length}
                    onPageChange={setCurrentPage}
                    label="casos"
                    accentClass="text-slate-900 bg-slate-100 border-slate-200"
                />
            </main>

            {/* MODALES RECONECTADOS */}
            {activeItem && (
                <>
                    <ModalEmision 
                        show={showEditPoliza} 
                        onClose={() => { unlockPoliza(activeItem.path); setShowEditPoliza(false); setActiveItem(null); }} 
                        initialData={activeItem} 
                        clientData={{ id: activeItem.clienteId, nombre: activeItem.displayNombre }} 
                        onSaveSuccess={() => { unlockPoliza(activeItem.path); setShowEditPoliza(false); setActiveItem(null); }} 
                    />
                    <ModalEdicionContrato 
                        show={showEditArt} 
                        onClose={() => { unlockPoliza(activeItem.path); setShowEditArt(false); setActiveItem(null); }} 
                        contrato={activeItem} 
                        clientData={{ id: activeItem.clienteId, nombre: activeItem.displayNombre }} 
                        onSuccess={() => { unlockPoliza(activeItem.path); setShowEditArt(false); setActiveItem(null); }} 
                    />
                    <ModalDetallePoliza 
                        show={showDetail} 
                        onClose={() => { setShowDetail(false); setActiveItem(null); }} 
                        poliza={activeItem} 
                    />
                </>
            )}
        </div>
    );
};

export default TableroPendientes;